import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';

import icon_lightmasterLight from '../../imgs/icon-lightmasterLight.png';
import icon_lightmasterLight_disabled from '../../imgs/icon-lightmasterLight-disabled.png';
import icon_solarLight from '../../imgs/icon-solarLight.png';
import icon_solarLight_disabled from '../../imgs/icon-solarLight-disabled.png';
import icon_workshopLighting from '../../imgs/icon-workshopLighting.png';
import icon_workshopLighting_disabled from '../../imgs/icon-workshopLighting-disabled.png';
import switchOnPng from '../../imgs/switch-on.png';
import switchOffPng from '../../imgs/switch-off.png';

// @ts-ignore
import kkmapgl from '@/assets/sdk/kkmap-gl.es.js';
// @ts-ignore
import '@/assets/sdk/kkmap-gl.css';
import { getAssetBaseUrl } from '@/utils/utils';

import { useRequest } from 'ahooks';
import {
  apiV2ElectricMapDataGgdDataPost,
  apiV2GgdBatchOperateStatusPost,
  apiV2LightmapAreaPost,
  apiV2LightmapSunlightDevicePagePost,
  V2LightmapAreaPostResponse,
  V2LightmapSunlightDevicePagePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { createDom, createLightPopupHtml, createPulsingDot, createSunLightPopupHtml } from './mapUtils';
import { message, Modal } from '@maxtropy/components';
import WorkshopPanel from '../WorkshopLightPanel';
import SunLightPanel from '../SunLightPanel';
import { loadImageAsync, mapExtensionModule } from '@/pages/WgqAllMaps/components/UnityPowerMap/components/MapView/map';

interface MapViewProps {
  className?: string;
  children?: React.ReactNode;
}

const baseUrl = getAssetBaseUrl();

export type AreaItem = Exclude<V2LightmapAreaPostResponse['list'], undefined>[number];
export type SunLightItem = Exclude<V2LightmapSunlightDevicePagePostResponse['list'], undefined>[number];

const MapView: FC<MapViewProps> = props => {
  const { className, children } = props;
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const popupsRef = useRef<
    {
      popup: any;
      area: AreaItem;
    }[]
  >([]);
  const [markerVisible, setMarkerVisible] = useState({
    ggdLight: true,
    sunLight: true,
    workshopLight: true,
  });
  // const [is2D, setIs2D] = useState<boolean>(true); // 2/3D切换
  const [lightVisible, setLightVisible] = useState<boolean>(false); // 太阳能路灯
  const [workSpaceLightVisible, setWorkSpaceLightVisible] = useState<boolean>(false); // 厂区照明
  const [record, setRecord] = useState<AreaItem>();
  const popup = useRef<any>(null);
  const map = useRef<any>(null);

  // 查询区域位置数据
  const { data: areaList } = useRequest(async () => {
    const res = await apiV2LightmapAreaPost({});
    return res?.list ?? [];
  });

  // 1、高杆灯
  const ggdList = useMemo(() => areaList?.filter(a => a.existGgd), [areaList]);
  // 2、太阳能路灯
  const sunList = useMemo(() => areaList?.filter(a => a.existSunlight), [areaList]);
  // 3、车间照明
  const lightList = useMemo(() => areaList?.filter(a => a.existCircuit), [areaList]);

  // 初始化
  useEffect(() => {
    if (map.current) {
      map.current.remove();
      map.current = null;
      popupsRef.current.forEach(p => p.popup.remove());
      popupsRef.current = [];
      popup.current?.remove();
      popup.current = null;
    }
    setMapLoaded(false);

    if (!window.kkmapgl) window.kkmapgl = kkmapgl;
    if (!window.mapgl) window.mapgl = kkmapgl;

    map.current = new kkmapgl.Map({
      container: 'map',
      minZoom: 15,
      maxZoom: 21,
      mapType: '3d-dark',
      style: {
        version: 8,
        sources: {},
        layers: [],
      },
      zoom: 17.1143,
      bearing: 60.365,
      pitch: 66.4901,
      center: [121.62600259202594, 31.33855999677496],

      showPage: true,
      env3D: {
        defaultLights: false,
      },
      dracoPath: baseUrl + '/draco/',
      defaultModel: {
        type: 'glb',
        url: baseUrl + '/models/091501.glb',
        draco: true,
        ktx2Parse: true,
        useMeshOpt: true,
      },
    });
    map.current.showLoading(false);
    const mapExtension = mapExtensionModule();
    mapExtension.setMapInstance(map.current);

    map.current.on('load', function () {
      // handleClickFit();
      setMapLoaded(true);
    });

    map.current.on('click', function (e: any) {
      console.log('A click event has occurred at ' + e.lngLat);
    });
    map.current.on('click', function (e: any) {
      console.log('地图zoom', map.current.getZoom());
      console.log('地图bearing', map.current.getBearing());
      console.log('地图pitch', map.current.getPitch());
      console.log('地图中心', map.current.getCenter());
    });
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
        popupsRef.current.forEach(p => p.popup.remove());
        popupsRef.current = [];
        popup.current?.remove();
        popup.current = null;
      }
    };
  }, []);

  const handleClickFit = () => {
    if (map.current) {
      map.current.fitBounds(
        [
          [121.6375834482975, 31.34077687819034],
          [121.6230903232513, 31.33970415505882],
        ],
        { padding: 0, bearing: 29, pitch: 60, maxZoom: 16, offset: [60, -240] }
      );
    }
  };

  const createPopupMarker = (item: AreaItem) => {
    const el = document.createElement('div');
    el.innerHTML = createDom(item);

    const markerPopup = new kkmapgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: [0, -10],
      className: 'area-marker',
    })
      .setLngLat([item.pointLng, item.pointLat])
      .setDOMContent(el)
      .addTo(map.current);

    // 鼠标悬浮事件
    const popupElem = markerPopup.getElement();
    const targetTip = popupElem.querySelector(`#marker-outer-${item.id}`) as HTMLDivElement | null;
    if ((item?.circuitData?.count ?? 0) <= 3 && item.existCircuit) {
      el?.addEventListener('mouseenter', () => {
        if (targetTip) {
          targetTip!.style.display = 'flex';
        }
      });
      el?.addEventListener('mouseleave', () => {
        if (targetTip) {
          targetTip!.style.display = 'none';
        }
      });
    } else {
      if (targetTip) {
        targetTip.style.display = 'flex';
      }
    }

    // 点击事件（太阳能路灯）
    // if (item.existSunlight) {
    //   el.addEventListener('click', () => {
    //     // setWorkSpaceLightVisible(false);
    //     setLightVisible(true);
    //     setRecord(item);
    //   });
    // }
    // 点击事件（厂区照明）
    if (item.existCircuit) {
      el.addEventListener('click', () => {
        // setLightVisible(false);
        setWorkSpaceLightVisible(true);
        setRecord(item);
      });
    }
    popupsRef.current.push({
      popup: markerPopup,
      area: item,
    });
  };

  // 初始化高杆灯资源
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const images: [string, string][] = [
      ['icon-light-on', `${baseUrl}/light-location-on@2x.png`],
      ['icon-line-on', `${baseUrl}/line-on@2x.png`],
      ['icon-bottom-on', `${baseUrl}/bottom-on@2x.png`],
      ['icon-bottom-off', `${baseUrl}/bottom-off@2x.png`],
      ['icon-line-off', `${baseUrl}/line-off@2x.png`],
      ['icon-line-offline', `${baseUrl}/line-offline@2x.png`],
      ['icon-light-off', `${baseUrl}/light-location-off@2x.png`],
      ['icon-light-offline', `${baseUrl}/light-location-offline@2x.png`],
      ['icon-sun-light-on', `${baseUrl}/solarlight-location@2x.png`],
      ['icon-sun-light-off', `${baseUrl}/solarlight-location-off@2x.png`],
      ['icon-sun-light-offline', `${baseUrl}/solarlight-location-offline@2x.png`],
      ['icon-sun-light-line', `${baseUrl}/solarlight-line@2x.png`],
      ['icon-sun-light-line-offline', `${baseUrl}/solarlight-line-offline@2x.png`],
      ['icon-sun-light-bottom', `${baseUrl}/solarlight-bottom@2x.png`],
    ];

    const init = async () => {
      try {
        await Promise.all(images.map(([name, url]) => loadImageAsync(map.current, name, url)));
        // 注册光圈资源
        if (!map.current.hasImage('pulsing-dot')) {
          const pulsingDot = createPulsingDot(220, '87,251,139', map);
          map.current.addImage('pulsing-dot', pulsingDot, { pixelRatio: 2 });
        }

        setTimeout(() => {
          let source_ggd = map.current?.getSource('geojson-ggd');
          let source_sunLight = map.current?.getSource('geojson-sunLight');
          if (!source_ggd && ggdList && ggdList.length > 0) {
            // 高杆灯
            map.current.addSource('geojson-ggd', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: (ggdList ?? []).map((k, index) => ({
                  id: index,
                  type: 'Feature',
                  geometry: {
                    type: 'Point',
                    coordinates: [Number(k.pointLng), Number(k.pointLat)],
                  },
                  properties: {
                    ...k,
                    // 根据 ggdData 的数量判断状态：优先显示离线，其次合闸，最后分闸
                    ggdStatusLabel:
                      (k.ggdData?.offlineCount ?? 0) > 0 ? 'offline' : (k.ggdData?.openCount ?? 0) > 0 ? 'on' : 'off',
                  },
                })),
              },
            });

            source_ggd = map.current?.getSource('geojson-ggd');
          }

          if (!source_sunLight && sunList && sunList.length > 0) {
            // 太阳能路灯
            map.current.addSource('geojson-sunLight', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: (sunList ?? []).map((k, index) => ({
                  id: index,
                  type: 'Feature',
                  geometry: {
                    type: 'Point',
                    coordinates: [Number(k.pointLng), Number(k.pointLat)],
                  },
                  properties: {
                    ...k,
                    // 根据 lightData 的数量判断状态：优先显示离线，其次合闸，最后分闸
                    sunLightStatusLabel:
                      (k.lightData?.offlineCount ?? 0) > 0
                        ? 'offline'
                        : (k.lightData?.openCount ?? 0) > 0
                        ? 'on'
                        : 'off',
                  },
                })),
              },
            });
            source_sunLight = map.current?.getSource('geojson-sunLight');
          }
          if (ggdList && ggdList.length > 0) {
            // 添加高杆灯
            addLightLayer();
          }
          if (sunList && sunList.length > 0) {
            // 添加太阳能路灯
            addSunLightLayer();
          }
        }, 7000);
      } catch (err) {
        console.error('图片加载失败:', err);
      }
    };
    init();
  }, [mapLoaded, sunList, ggdList]);

  useEffect(() => {
    if (!map.current) return;
    if (!mapLoaded || !lightList?.length) return;

    setTimeout(() => {
      // 车间照明
      if (lightList && lightList.length > 0) {
        lightList.forEach(item => createPopupMarker(item));
      }
    }, 1000);
  }, [mapLoaded, lightList, sunList]);

  // 显示与隐藏
  const handleToggleMarker = (type: 'ggdLight' | 'sunLight' | 'workshopLight') => {
    if (popup.current) {
      popup.current?.remove();
      popup.current = null;
    }
    // 高杆灯
    if (type === 'ggdLight') {
      addLight();
    } else if (type === 'sunLight') {
      // 太阳能
      addSunLightPopUp();
    } else {
      // 照明
      addLightPopUp();
    }
    setMarkerVisible(prev => ({ ...prev, [type]: !prev[type] }));
  };

  // 高杆灯显示与隐藏
  const addLight = () => {
    if (markerVisible.ggdLight) {
      if (map.current.getLayer('light')) {
        map.current.removeLayer('light');
      }
      if (map.current.getLayer('light-line')) {
        map.current.removeLayer('light-line');
      }
      if (map.current.getLayer('bottom')) {
        map.current.removeLayer('bottom');
      }
    } else {
      addLightLayer();
    }
  };

  // 点击高杆灯的灯
  const clickLightIcon = (e: any) => {
    if (!e.features?.length) return;
    const feature = e.features[0];

    // 获取经纬度
    const [lng, lat] = feature.geometry.coordinates as [number, number];

    // 如果之前已有光圈，先移除
    if (map.current.getLayer('light-pulse')) {
      map.current.removeLayer('light-pulse');
    }
    if (map.current.getSource('light-pulse')) {
      map.current.removeSource('light-pulse');
    }

    // 添加一个临时 source
    map.current.addSource('light-pulse', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [lng, lat] },
          },
        ],
      },
    });

    map.current.addLayer({
      id: 'light-pulse',
      type: 'symbol',
      source: 'light-pulse',
      layout: {
        'icon-image': 'pulsing-dot',
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-offset': [0, -110],
      },
    });
    // 将light-pulse图层置于light图层上方
    map.current.moveLayer('light-pulse', 'light');

    if (feature.properties) {
      apiV2ElectricMapDataGgdDataPost({ id: feature.properties.id }).then(res => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = createLightPopupHtml(res);
        if (popup.current) {
          popup.current.remove();
          popup.current = null;
        }

        popup.current = new kkmapgl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: [20, -200],
          className: 'light-popup',
        })
          .setLngLat([lng, lat])
          .setDOMContent(wrapper)
          .addTo(map.current);
        setTimeout(() => {
          const closeBtn = wrapper.querySelector<HTMLDivElement>('#closePopupBtn');
          if (closeBtn) {
            closeBtn.addEventListener('click', () => {
              popup.current?.remove();
              popup.current = null;
              if (map.current.getLayer('light-pulse')) map.current.removeLayer('light-pulse');
              if (map.current.getSource('light-pulse')) map.current.removeSource('light-pulse');
            });
          }
          const statusSwitch = wrapper.querySelector<HTMLDivElement>('#status-switch');
          if (statusSwitch) {
            statusSwitch.addEventListener('click', () => {
              Modal.confirm({
                title: (
                  <span style={{ color: 'var(--mx-text-base-color)' }}>
                    确定要下发<span style={{ color: '#E64242' }}>{res.intStatus === 1 ? '远程关闭' : '远程开启'}</span>
                    指令到{res.name}设备吗？
                  </span>
                ),
                okText: '确定',
                onOk() {
                  const newStatus = res.intStatus === 1 ? 0 : 1;
                  apiV2GgdBatchOperateStatusPost({
                    deviceIds: JSON.parse(feature.properties.ggdDeviceIds),
                    status: newStatus,
                  })
                    .then(() => {
                      const source: any = map.current.getSource('geojson-ggd');
                      message.success('下发成功');
                      if (source) {
                        const data = (typeof source.getData === 'function'
                          ? source.getData()
                          : source._data ?? null) ?? {
                          type: 'FeatureCollection',
                          features: [],
                        };

                        const updated = {
                          ...data,
                          features: (data.features || []).map((f: any) => {
                            if (
                              f.properties &&
                              f?.properties?.ggdDeviceIds?.[0] === JSON.parse(feature?.properties?.ggdDeviceIds)?.[0]
                            ) {
                              return {
                                ...f,
                                properties: {
                                  ...f.properties,
                                  ggdStatusLabel: newStatus === 1 ? 'on' : 'off',
                                  intStatus: newStatus,
                                },
                              };
                            }
                            return f;
                          }),
                        };
                        popup.current?.remove();
                        popup.current = null;
                        if (map.current.getLayer('light-pulse')) map.current.removeLayer('light-pulse');
                        if (map.current.getSource('light-pulse')) map.current.removeSource('light-pulse');
                        try {
                          source.setData(updated);
                        } catch (err) {
                          console.warn('source.setData failed:', err);
                        }
                      }

                      const statusImg = wrapper.querySelector<HTMLImageElement>('#status-switch');
                      if (statusImg) {
                        statusImg.src = newStatus ? switchOnPng : switchOffPng;
                      }
                    })
                    .catch(err => {
                      console.error('下发状态失败', err);
                    });
                },
              });
            });
          }
        }, 300);
      });
    }
  };

  // 点击太阳能路灯
  const clickSunLightIcon = (e: any) => {
    if (!e.features?.length) return;
    const feature = e.features[0];

    // 获取经纬度
    const [lng, lat] = feature.geometry.coordinates as [number, number];

    // 如果之前已有光圈，先移除
    if (map.current.getLayer('light-pulse')) {
      map.current.removeLayer('light-pulse');
    }
    if (map.current.getSource('light-pulse')) {
      map.current.removeSource('light-pulse');
    }

    // 添加一个临时 source
    map.current.addSource('light-pulse', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [lng, lat] },
          },
        ],
      },
    });

    map.current.addLayer({
      id: 'light-pulse',
      type: 'symbol',
      source: 'light-pulse',
      layout: {
        'icon-image': 'pulsing-dot',
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-offset': [0, -110],
      },
    });
    // 将light-pulse图层置于light图层上方
    map.current.moveLayer('light-pulse', 'light_sunLight');

    if (feature.properties) {
      apiV2LightmapSunlightDevicePagePost({
        id: feature.properties.id,
        deviceIds: JSON.parse(feature.properties.lightDeviceIds),
        page: 1,
        size: 1,
      }).then(res => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = createSunLightPopupHtml(res?.list?.[0]);
        if (popup.current) {
          popup.current.remove();
          popup.current = null;
        }

        popup.current = new kkmapgl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: [20, -200],
          className: 'light-popup',
        })
          .setLngLat([lng, lat])
          .setDOMContent(wrapper)
          .addTo(map.current);
        setTimeout(() => {
          const closeBtn = wrapper.querySelector<HTMLDivElement>('#closePopupBtn');
          const moreBtn = wrapper.querySelector<HTMLDivElement>('#more');
          if (closeBtn) {
            closeBtn.addEventListener('click', () => {
              popup.current?.remove();
              popup.current = null;
              if (map.current.getLayer('light-pulse')) map.current.removeLayer('light-pulse');
              if (map.current.getSource('light-pulse')) map.current.removeSource('light-pulse');
            });
          }
          if (moreBtn) {
            moreBtn.addEventListener('click', () => {
              window.open(`/data/history/device?deviceId=${res?.list?.[0].deviceId}`, '_blank');
            });
          }
        }, 300);
      });
    }
  };

  // 添加高杆灯图层
  const addLightLayer = () => {
    if (!map.current.getLayer('light')) {
      map.current?.addLayer({
        id: 'light',
        type: 'symbol',
        source: 'geojson-ggd',
        layout: {
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-image': [
            'match',
            ['get', 'ggdStatusLabel'],
            'on',
            'icon-light-on',
            'off',
            'icon-light-off',
            'offline',
            'icon-light-offline',
            'icon-light-off',
          ],
          'icon-rotation-alignment': 'viewport',
          'icon-pitch-alignment': 'viewport',
          'icon-size': 0.5,
          'icon-offset': [0, -96 / 0.5 - 20],
        },
      });
      map.current.on('click', 'light', clickLightIcon);
    }
    if (!map.current.getLayer('light-line')) {
      map.current?.addLayer({
        id: 'light-line',
        type: 'symbol',
        source: 'geojson-ggd',
        layout: {
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-image': [
            'match',
            ['get', 'ggdStatusLabel'],
            'on',
            'icon-line-on',
            'off',
            'icon-line-off',
            'offline',
            'icon-line-offline',
            'icon-line-off',
          ],
          'icon-rotation-alignment': 'viewport',
          'icon-pitch-alignment': 'viewport',
          'icon-size': 0.5,
          'icon-anchor': 'bottom',
        },
      });
    }
    if (!map.current.getLayer('bottom')) {
      // 底座
      map.current?.addLayer({
        id: 'bottom',
        type: 'symbol',
        source: 'geojson-ggd',
        layout: {
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-image': [
            'match',
            ['get', 'ggdStatusLabel'],
            'on',
            'icon-bottom-on',
            'off',
            'icon-bottom-off',
            'offline',
            'icon-bottom-off',
            'icon-bottom-off',
          ],
          'icon-rotation-alignment': 'viewport',
          'icon-pitch-alignment': 'viewport',
          'icon-size': 0.5,
        },
      });
    }
  };

  // 添加太阳能路灯图层
  const addSunLightLayer = () => {
    if (!map.current.getLayer('light_sunLight')) {
      map.current?.addLayer({
        id: 'light_sunLight',
        type: 'symbol',
        source: 'geojson-sunLight',
        layout: {
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-image': [
            'match',
            ['get', 'sunLightStatusLabel'],
            'on',
            'icon-sun-light-on',
            'off',
            'icon-sun-light-off',
            'offline',
            'icon-sun-light-offline',
            'icon-sun-light-off',
          ],
          'icon-rotation-alignment': 'viewport',
          'icon-pitch-alignment': 'viewport',
          'icon-size': 0.5,
          'icon-offset': [0, -96 / 0.5 - 20],
        },
      });
      // 点击图层
      map.current.on('click', 'light_sunLight', clickSunLightIcon);
    }
    if (!map.current.getLayer('sunLight_line')) {
      map.current?.addLayer({
        id: 'sunLight_line',
        type: 'symbol',
        source: 'geojson-sunLight',
        layout: {
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-image': [
            'match',
            ['get', 'sunLightStatusLabel'],
            'on',
            'icon-sun-light-line',
            'off',
            'icon-sun-light-line',
            'offline',
            'icon-sun-light-line-offline',
            'icon-sun-light-line',
          ],
          'icon-rotation-alignment': 'viewport',
          'icon-pitch-alignment': 'viewport',
          'icon-size': 0.5,
          'icon-anchor': 'bottom',
        },
      });
    }
    if (!map.current.getLayer('sunLight_bottom')) {
      // 底座
      map.current?.addLayer({
        id: 'sunLight_bottom',
        type: 'symbol',
        source: 'geojson-sunLight',
        layout: {
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-image': 'icon-sun-light-bottom',
          'icon-rotation-alignment': 'viewport',
          'icon-pitch-alignment': 'viewport',
          'icon-size': 0.5,
        },
      });
    }
  };

  // 太阳能路灯的显示与隐藏
  const addSunLightPopUp = () => {
    if (markerVisible.sunLight) {
      if (map.current.getLayer('light_sunLight')) {
        map.current.removeLayer('light_sunLight');
      }
      if (map.current.getLayer('sunLight_line')) {
        map.current.removeLayer('sunLight_line');
      }
      if (map.current.getLayer('sunLight_bottom')) {
        map.current.removeLayer('sunLight_bottom');
      }
    } else {
      addSunLightLayer();
    }
  };

  // 照明的显示与隐藏
  const addLightPopUp = () => {
    if (!markerVisible.workshopLight) {
      lightList?.forEach(area => {
        createPopupMarker(area);
      });
    } else {
      popupsRef.current = popupsRef.current.filter(item => {
        if (item.area.existCircuit) {
          item.popup?.remove(); // 清理掉 popup
          return false; // 不保留
        }
        return true; // 保留
      });
    }
  };

  // 2/3D切换
  // const change23D = () => {
  //   if (map.getMapType() === '3d-dark') {
  //     map.setMapType('2d');
  //   } else {
  //     map.setMapType('3d-dark');
  //   }
  //   setIs2D(!is2D);
  // };

  return (
    <div className={`${styles.map} ${className || ''}`} id="map">
      <div className={styles.legendList} style={{ left: '520px' }}>
        <div
          className={`${markerVisible.ggdLight ? styles.activeGgdItem : styles.inActiveItem}`}
          onClick={() => handleToggleMarker('ggdLight')}
        >
          <img src={markerVisible.ggdLight ? icon_lightmasterLight : icon_lightmasterLight_disabled} alt="" />
          <div>高杆灯</div>
        </div>

        <div
          className={`${markerVisible.sunLight ? styles.activeSunLightItem : styles.inActiveItem}`}
          onClick={() => handleToggleMarker('sunLight')}
        >
          <img src={markerVisible.sunLight ? icon_solarLight : icon_solarLight_disabled} alt="" />
          <div>太阳能路灯</div>
        </div>

        <div
          className={`${markerVisible.workshopLight ? styles.activeLightItem : styles.inActiveItem}`}
          onClick={() => handleToggleMarker('workshopLight')}
        >
          <img src={markerVisible.workshopLight ? icon_workshopLighting : icon_workshopLighting_disabled} alt="" />
          <div>车间照明</div>
        </div>
      </div>
      {/* <div className={styles.twoThree_img} onClick={() => change23D()} style={{ right: '520px' }}>
        <img src={is2D ? threeD : twoD} alt="切换2/3D" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div> */}
      {children}
      {/* 车间照明 */}
      <Modal
        open={workSpaceLightVisible}
        onCancel={() => {
          setRecord(undefined);
          setWorkSpaceLightVisible(false);
        }}
        width={1200}
        footer={null}
        className={styles.modal_sty}
        destroyOnClose
        getContainer={() => document.getElementById('full_screen_box') as HTMLElement}
      >
        <WorkshopPanel zoneName={record?.zoneName} workSpaceLightVisible={workSpaceLightVisible} />
      </Modal>
      {/* 太阳能路灯 */}
      <Modal
        open={lightVisible}
        onCancel={() => {
          setRecord(undefined);
          setLightVisible(false);
        }}
        width={1200}
        footer={null}
        className={styles.modal_sty}
        destroyOnClose
        getContainer={() => document.getElementById('full_screen_box') ?? document.body}
      >
        <SunLightPanel record={record} lightVisible={lightVisible} />
      </Modal>
    </div>
  );
};

export default MapView;
